import { createContext, useContext, type MutableRefObject } from 'react'
import useIsomorphicLayoutEffect from 'hooks/useIsomorphicLayoutEffect'
import type { MarkerSelectionData } from 'tripPlanner/components/Chat/chatMapConstants'
import type { RightPanelView } from '../hooks/useRightPanelState'
import type { DrawerStackEntry } from '../hooks/useDrawerStackState'

export interface CartItemPrice {
  itemId: string
  // What the List row currently shows -- reflects the page's own LuxPlus preview toggle,
  // which any customer can flip regardless of real membership. May not match what checkout
  // will actually charge.
  displayedPrice: string
  // What this customer will actually be charged -- reflects real LuxPlus entitlement,
  // matching the cart panel and navbar cart total, not the preview toggle.
  checkoutPrice: string
  // Whether this item's price counts toward the total the customer is quoted --
  // matches computeCartPanelTotal's own eligibility check (cartExcluded/includeInTotal)
  // exactly, so the assistant can reconstruct the real total instead of over-counting
  // customer-excluded or not-yet-bookable items.
  countsTowardTotal: boolean
  // Whether this item is fully configured in an active cart entry (dates/room/occupancy
  // etc. all selected) -- when true, displayedPrice/checkoutPrice are the accurate charge
  // for what's currently selected. When false, the item isn't in the cart yet (or is, but
  // still missing a selection), so the price is only an indicative starting figure (e.g.
  // "from $250/night") that's expected to change once the customer finalises their pick --
  // matches TripListRowPrice's own "From" prefix exactly. Without this, the assistant has
  // no way to tell a locked-in price from one that's still expected to move.
  isBookingReady: boolean
  // A short reason this item can't be checked out yet (e.g. "Dates required"), matching
  // the same cartStatus the cart panel tiles use for their own warning banners. Undefined
  // when the item is already checkout-ready -- absence means ready, not "unknown."
  checkoutBlocker?: string
}

export interface TurnAuthoritySnapshot {
  rightPanelView: RightPanelView | undefined
  selectedMarkerData: MarkerSelectionData | undefined
  isAnyDrawerOpen: boolean
  topEntry: DrawerStackEntry | undefined
  cartItemPrices: Array<CartItemPrice>
}

export const DEFAULT_TURN_AUTHORITY_SNAPSHOT: TurnAuthoritySnapshot = {
  rightPanelView: undefined,
  selectedMarkerData: undefined,
  isAnyDrawerOpen: false,
  topEntry: undefined,
  cartItemPrices: [],
}

export const TurnAuthoritySnapshotContext = createContext<MutableRefObject<TurnAuthoritySnapshot> | undefined>(undefined)

/**
 * Returns a stable ref (not state) holding the send-time right-panel/drawer snapshot -- read
 * imperatively via `.current`, never during render. A future `resolveTurnAuthority` callback
 * (Phase 1) reads this at call time to populate `visibleState`; this PR only wires it through.
 */
export function useTurnAuthoritySnapshot(): MutableRefObject<TurnAuthoritySnapshot> {
  const context = useContext(TurnAuthoritySnapshotContext)
  if (!context) {
    throw new Error('useTurnAuthoritySnapshot must be used within TripChatRuntime')
  }

  return context
}

interface RightPanelSnapshotRegistrationProps {
  rightPanelView: RightPanelView
  selectedMarkerData: MarkerSelectionData | undefined
}

export function RightPanelSnapshotRegistration({
  rightPanelView,
  selectedMarkerData,
}: RightPanelSnapshotRegistrationProps) {
  const snapshotRef = useTurnAuthoritySnapshot()

  useIsomorphicLayoutEffect(() => {
    snapshotRef.current.rightPanelView = rightPanelView
    snapshotRef.current.selectedMarkerData = selectedMarkerData
  }, [snapshotRef, rightPanelView, selectedMarkerData])

  // Separate effect, empty deps -- runs its cleanup only on true unmount, not on every value
  // change. RightPanelContainer has no mobile counterpart (desktop-only mount point), and the
  // desktop/mobile surface can flip at runtime (viewport resize/rotation), not just on initial
  // load -- without this, these fields would freeze at their last desktop values indefinitely
  // once the surface switches to mobile.
  useIsomorphicLayoutEffect(() => () => {
    snapshotRef.current.rightPanelView = DEFAULT_TURN_AUTHORITY_SNAPSHOT.rightPanelView
    snapshotRef.current.selectedMarkerData = DEFAULT_TURN_AUTHORITY_SNAPSHOT.selectedMarkerData
  }, [snapshotRef])

  return null
}

interface DrawerSnapshotRegistrationProps {
  isAnyDrawerOpen: boolean
  topEntry: DrawerStackEntry | undefined
}

export function DrawerSnapshotRegistration({ isAnyDrawerOpen, topEntry }: DrawerSnapshotRegistrationProps) {
  const snapshotRef = useTurnAuthoritySnapshot()

  useIsomorphicLayoutEffect(() => {
    snapshotRef.current.isAnyDrawerOpen = isAnyDrawerOpen
    snapshotRef.current.topEntry = topEntry
  }, [snapshotRef, isAnyDrawerOpen, topEntry])

  // Lower risk than RightPanelSnapshotRegistration's unmount case -- DrawerContainer's desktop
  // and mobile mount points are mutually exclusive but always have exactly one active instance
  // whenever any Trip Chat surface is rendered, so a breakpoint flip always has a replacement
  // writer in the same commit. Reset on unmount anyway for consistency/defense-in-depth.
  useIsomorphicLayoutEffect(() => () => {
    snapshotRef.current.isAnyDrawerOpen = DEFAULT_TURN_AUTHORITY_SNAPSHOT.isAnyDrawerOpen
    snapshotRef.current.topEntry = DEFAULT_TURN_AUTHORITY_SNAPSHOT.topEntry
  }, [snapshotRef])

  return null
}

interface CartPricingSnapshotRegistrationProps {
  cartItemPrices: Array<CartItemPrice>
}

export function CartPricingSnapshotRegistration({ cartItemPrices }: CartPricingSnapshotRegistrationProps) {
  const snapshotRef = useTurnAuthoritySnapshot()

  useIsomorphicLayoutEffect(() => {
    snapshotRef.current.cartItemPrices = cartItemPrices
  }, [snapshotRef, cartItemPrices])

  // On desktop, ListContainer stays mounted continuously (visible only gates its JSX), so
  // this only resets on a genuine unmount -- a desktop/mobile surface flip. On mobile,
  // ListContainer mounts only while the list screen is the active full-screen tab (mobile
  // views are separate screens, not overlaid), so this reset also fires on every mobile
  // screen switch away from list -- correct there too, since prices genuinely stop being
  // computed rather than merely being hidden.
  useIsomorphicLayoutEffect(() => () => {
    snapshotRef.current.cartItemPrices = DEFAULT_TURN_AUTHORITY_SNAPSHOT.cartItemPrices
  }, [snapshotRef])

  return null
}
